import { executeScriptInWorker, get_org_id } from 'liyu-pc-base/utils/function';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { resolveContextValue } from 'liyu-pc-base/components/FormRender/utils';

/** 模块级防重入：serial 分配中标记（key: 字段 name + 行路径），避免重复请求后端 */
const serialAllocatingKeys = new Set<string>();

function getSerialKey(field: any, baseKeyPath: any[] = []): string {
  return [...baseKeyPath, field.name || field.code || 'field'].join('__');
}

export { getSerialKey };

/** 判断某行 serial 分配是否进行中（供批量分配跳过被单行请求占用的行） */
export function isSerialKeyAllocating(key: string): boolean {
  return serialAllocatingKeys.has(key);
}

/** 批量占位：全部 key 空闲才占位成功（与单行分配 computeDynamicValue 守卫2 互斥） */
export function acquireSerialKeys(keys: string[]): boolean {
  if (keys.some((k) => serialAllocatingKeys.has(k))) return false;
  keys.forEach((k) => serialAllocatingKeys.add(k));
  return true;
}

/** 释放批量占位 */
export function releaseSerialKeys(keys: string[]): void {
  keys.forEach((k) => serialAllocatingKeys.delete(k));
}

export interface SerialBatchResult {
  codes?: string[];
  error?: string;
}

/**
 * 批量分配 count 个流水号（所有 serial 模式通用 M1-M5）。
 *
 * 一次请求预占用 count 个号码，替代逐行 N 次请求：
 * - M5 覆盖ID：后端逐个 uuid4 落库 ApproveSerialUuid 预占用；
 * - M1-M4：后端单次锁内推进 count 个号码。
 * 后端约定：count > 1 返回列表（顺序即分配顺序）；count <= 1 返回单个字符串。
 * 两种形态在此统一归一化为数组，调用方只按数组消费。
 */
export async function allocateSerialBatch(field: any, count: number): Promise<SerialBatchResult> {
  const serialConfig = field.serialConfig || {};
  try {
    const res = await getApiData(
      {
        org_id: get_org_id(),
        mode: serialConfig.mode || 'M1',
        kwargs: JSON.stringify(serialConfig),
        count,
      },
      '/approve/get_approve_serial_number',
      false,
    );
    // ★ 后端约定（PSMSystem2 approve/views.py get_approve_serial_number）：
    //   count > 1 → allocate_serial_number_batch 返回列表；
    //   count <= 1 → allocate_serial_number 返回单个字符串（向后兼容既有单号调用方）。
    //   这里统一归一化成数组（空值由 filter 剔除），否则 count === 1 时会被判为
    //   「返回数量不符」而整轮跳过分配，表现为流水号一直为空、直到下一次触发才补生成。
    const codes = (Array.isArray(res) ? res : [res]).filter(
      (code: any) => code !== undefined && code !== null && code !== '',
    );
    if (codes.length !== count) {
      return { error: '流水号批量分配返回数量不符' };
    }
    return { codes };
  } catch (e: any) {
    console.error(e);
    return { error: e?.message || '流水号批量分配失败' };
  }
}

export interface ComputeDynamicValueParams {
  /** dynamicvaluefield 字段配置 */
  field: any;
  /** 当前值（用于判断是否需要更新） */
  currentValue: any;
  /** 行级 innerModel（表格模式下为每行的 innerModel） */
  innerModel?: any;
  /** 全局 innerModel */
  globalInnerModel?: any;
  /** 表单模型（表格模式下为 value 数组的每一条数据） */
  formModel?: any;
  /** 上下文 */
  context?: any;
  /** 基础 key 路径 */
  baseKeyPath?: any[];
  /** worker 宿主函数 */
  workerHostFunctions?: Record<string, any>;
  /** 是否为编辑/设计模式（serial 流水号在编辑模式下不调用后端分配） */
  is_form_design?: boolean;
  /** 结构锁定（修改模式）：serial M5 覆盖ID 在此状态下禁止自动分配，防止监听覆盖已落库覆盖ID */
  lock_structure?: boolean;
}

export interface ComputeDynamicValueResult {
  /** 计算后的新值 */
  value: any;
  /** code 模式执行出错时的错误信息 */
  error?: string;
}

/**
 * 调后端 preview 接口获取流水号示例（编辑/设计模式展示，不占用号码）。
 * 示例算法由后端 build_serial_code 统一维护，前端不重复实现。
 */
async function fetchSerialPreview(field: any): Promise<ComputeDynamicValueResult | null> {
  try {
    const serialConfig = field.serialConfig || {};
    const code = await getApiData(
      {
        org_id: get_org_id(),
        mode: serialConfig.mode || 'M1',
        kwargs: JSON.stringify(serialConfig),
        preview: true,
      },
      '/approve/get_approve_serial_number',
      false,
    );
    return { value: code };
  } catch (e: any) {
    // 失败：拦截器已自动弹错误提示，这里处理中断逻辑
    console.error(e);
    return { value: undefined, error: e?.message || '流水号预览失败' };
  }
}

/**
 * 计算 dynamicvaluefield 字段的值。
 *
 * 从 DynamicValueField.vue 的 syncValue 逻辑中提取，供 FormTemplate 表格模式复用。
 *
 * - 返回 { value } 表示需要更新值
 * - 返回 null 表示无需更新（值未变化或被跳过）
 */
export async function computeDynamicValue(
  params: ComputeDynamicValueParams,
): Promise<ComputeDynamicValueResult | null> {
  const {
    field,
    currentValue,
    innerModel,
    globalInnerModel,
    formModel,
    context,
    baseKeyPath = [],
    workerHostFunctions = {},
    is_form_design = false,
    lock_structure = false,
  } = params;

  if (field.watchType === 'scoped') {
    if (field.valueType === 'single') {
      let value = innerModel?.[field.fieldName];
      if (value === undefined && currentValue !== undefined) {
        return { value: '' };
      }
      if (field.valueKey) {
        value = value?.[field.valueKey];
      }
      if (value !== currentValue) {
        return { value };
      }
    } else if (field.valueType === 'multiple') {
      let value = innerModel?.[field.fieldName];
      if (field.valueKeyList && field.valueKeyList.length) {
        const tmpList = [];
        for (const item of field.valueKeyList) {
          if (value?.[item] !== undefined) {
            tmpList.push(value?.[item]);
          }
        }
        if (tmpList.length === 0 && currentValue !== undefined) {
          return { value: '' };
        }
        value = tmpList.join(field.valueSpace || ',');
        if (value !== currentValue) {
          return { value };
        }
      }
    }
  } else if (field.watchType === 'global') {
    if (field.valueType === 'single') {
      let value = globalInnerModel?.[field.fieldName];
      if (field.valueKey) {
        value = value?.[field.valueKey];
      }
      if (value === undefined && currentValue !== undefined) {
        return { value: '' };
      }
      if (value !== currentValue) {
        return { value };
      }
    } else if (field.valueType === 'multiple') {
      let value = globalInnerModel?.[field.fieldName];
      if (field.valueKeyList && field.valueKeyList.length) {
        const tmpList = [];
        for (const item of field.valueKeyList) {
          if (value?.[item]) {
            tmpList.push(value?.[item]);
          }
        }
        if (tmpList.length === 0 && currentValue !== undefined) {
          return null;
        }
        value = tmpList.join(field.valueSpace || ',');
        if (value !== currentValue) {
          return { value };
        }
      }
    }
  } else if (field.watchType === 'context') {
    const resolved = resolveContextValue(field.fieldName || '', context, [
      'formModel',
      ...baseKeyPath,
    ]);
    if (field.valueType === 'single') {
      let value = resolved;
      if (field.valueKey) {
        value = value?.[field.valueKey];
      }
      if (value === undefined && currentValue !== undefined) {
        return { value: '' };
      }
      if (value !== currentValue) {
        return { value };
      }
    } else if (field.valueType === 'multiple') {
      let value = resolved;
      if (field.valueKeyList && field.valueKeyList.length) {
        const tmpList = [];
        for (const item of field.valueKeyList) {
          if (value?.[item] !== undefined) {
            tmpList.push(value?.[item]);
          }
        }
        if (tmpList.length === 0 && currentValue !== undefined) {
          return { value: '' };
        }
        value = tmpList.join(field.valueSpace || ',');
        if (value !== currentValue) {
          return { value };
        }
      }
    }
  } else if (field.watchType === 'code') {
    try {
      const value = await executeScriptInWorker(
        field.code,
        {
          innerModel,
          globalInnerModel,
          field,
          formModel,
          context,
          keyPath: [...baseKeyPath, field.name],
        },
        workerHostFunctions,
      );
      if (value === undefined && currentValue !== undefined) {
        return { value: '' };
      }
      if (value !== currentValue) {
        return { value };
      }
    } catch (e: any) {
      console.error(e);
      return { value: '', error: e.message };
    }
  } else if (field.watchType === 'serial') {
    const serialConfig = field.serialConfig || {};
    // ★ 守卫-锁：修改模式（结构锁定 lock_structure）下，M5 覆盖ID（uuid）自动生成不执行。
    //    M5 用于生成「覆盖ID」标识，锁定态禁止 watch 监听重新分配，避免覆盖已落库的覆盖ID
    if (lock_structure && serialConfig.mode === 'M5') {
      return null;
    }
    // ★ 守卫0：编辑/设计模式（is_form_design）下调后端 preview 接口展示格式示例，
    //    不实际占用号码（示例算法由后端统一维护，前端不重复实现）
    if (is_form_design) {
      return await fetchSerialPreview(field);
    }
    // ★ 守卫1：已有值不重复分配（避免重复占用后端号码）
    if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
      return null;
    }
    // ★ 守卫2：防重入（同一字段同一行分配中，跳过本轮触发）
    const serialKey = getSerialKey(field, baseKeyPath);
    if (serialAllocatingKeys.has(serialKey)) {
      return null;
    }
    serialAllocatingKeys.add(serialKey);
    try {
      // getApiData 已解包 result：成功返回流水号字符串；失败拦截器自动弹提示并 reject
      const code = await getApiData(
        {
          org_id: get_org_id(),
          mode: serialConfig.mode || 'M1',
          kwargs: JSON.stringify(serialConfig),
        },
        '/approve/get_approve_serial_number',
        false,
      );
      if (code !== currentValue) {
        return { value: code };
      }
    } catch (e: any) {
      // 失败：拦截器已自动弹错误提示，这里处理中断逻辑
      console.error(e);
      return { value: undefined, error: e?.message || '流水号分配失败' };
    } finally {
      serialAllocatingKeys.delete(serialKey);
    }
  }

  return null;
}
